import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Menu,
  MenuItem,
  InputBase,
  IconButton,
  Checkbox
} from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import moment from 'moment'

/* ============================
   CONSTANTS
============================ */

const STATUS_OPTIONS = [
  "Ready for collection",
  "Collected by Driver",
  "Marked as Failed",
  "Successfully Delivered",
  "Returned to Pharmacy"
]

const headCells = [
  { id: 'surname', label: 'Recipient', filter: 'text' },
  { id: 'bagId', label: 'Bag ID', filter: 'text' },
  { id: 'additionalInfo', label: 'HDSS Activity info', filter: 'select' },
  { id: 'driver', label: 'Driver', filter: 'select' },
  { id: 'failureByDriver', label: 'Reason to Mark as Failed', filter: 'select' },
  { id: 'failureByInventory', label: 'Reason to Reject in Inventory', filter: 'select' },
  { id: 'status', label: 'Status', filter: 'select' },
  { id: 'timestamp', label: 'Last updated', sort: true }
]

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

/* ============================
   COMPONENT
============================ */

function BringgTable({ updates, groupByDriver }) {
  const classes = useStyles()
  const [order, setOrder] = React.useState('desc')

  const [filters, setFilters] = React.useState({})
  const [menuAnchor, setMenuAnchor] = React.useState(null)
  const [activeCell, setActiveCell] = React.useState(null)
  const [bagSearch, setBagSearch] = React.useState('')

  function isFilterActive(cellId) {
    const value = filters[cellId]
  
    if (!value) return false
    if (Array.isArray(value)) return value.length > 0
  
    return value !== ''
  }
  /* ============================
     DATA PREP
  ============================ */

  const baseRows = updates.filter(r => r.status !== 'Missing')

  const filteredRows = baseRows.filter(row =>
    Object.entries(filters).every(([key, value]) => {
  
      if (!value || (Array.isArray(value) && value.length === 0)) return true
  
      if (Array.isArray(value)) {
        return value.includes(row[key])
      }
      if (key === "bagId") {
        const normalize = str =>
          (str || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "")
      
        const search = normalize(value)
      
        if (!search) return true
      
        
        if (!row.barcodeEncoding) return false
      
        const bagValue = normalize(row.bagId)
      
        // optional: prevent noisy results
        if (search.length < 3) {
          return bagValue.includes(search)
        }
      
        return bagValue.startsWith(search)
      }
      return (row[key] || '')
        .toString()
        .toLowerCase()
        .includes(value.toLowerCase())
    })
  )

  // const sortedRows = [...filteredRows].sort((a, b) =>
  //   order === 'desc'
  //     ? moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf()
  //     : moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf()
  // )

  const sortedRows = [...filteredRows].sort((a, b) => {
    const timeDiff =
      moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf()
  
    if (timeDiff !== 0) return order === 'desc' ? timeDiff : -timeDiff
  
    
    return (b.bagId || '').localeCompare(a.bagId || '')
  })
  
  /* ============================
     MULTI-DRIVER DETECTION (PMR + BRANCH SAFE)
  ============================ */

  const orderDriverMap = React.useMemo(() => {
  const map = {}

  baseRows.forEach(r => {
    const orderKey =
      r.bagId ||
      r.barcodeLastFourChars ||
      r.additionalInfo ||
      `${r.surname}-${moment(r.timestamp).format('YYYY-MM-DD')}`

    if (!orderKey) return

    map[orderKey] = map[orderKey] || new Set()
    if (r.driver) map[orderKey].add(r.driver)
  })

  return map
}, [baseRows])

  function isMultiDriver(row) {
    const key =
      row.bagId ||
      row.barcodeLastFourChars ||
      row.additionalInfo ||
      `${row.surname}-${moment(row.timestamp).format('YYYY-MM-DD')}`

    return key && orderDriverMap[key]?.size > 1
  }

  /* ============================
     HELPERS
  ============================ */

  function toggleSort() {
    setOrder(order === 'desc' ? 'asc' : 'desc')
  }

  function openMenu(e, cell) {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
    setActiveCell(cell)
  
    
    setBagSearch(filters[cell.id] || '')
  }

  function closeMenu() {
    setMenuAnchor(null)
    setActiveCell(null)
  }

  function applyFilter(key, value) {
    setFilters({ ...filters, [key]: value })
    closeMenu()
  }
  function toggleMultiFilter(key, value) {
    const existing = filters[key] || []
  
    let updated
  
    if (existing.includes(value)) {
      updated = existing.filter(v => v !== value)
    } else {
      updated = [...existing, value]
    }
  
    setFilters({
      ...filters,
      [key]: updated
    })
  }

  function getDistinctValues(field) {
    return [
      ...new Set(
        baseRows
          .map(r => r[field])
          .filter(v => v && v !== '-')
      )
    ]
  }

  function groupBy(records) {
    return records.reduce((acc, r) => {
      const key = r.driver || 'Unassigned'
      acc[key] = acc[key] || []
      acc[key].push(r)
      return acc
    }, {})
  }

  const groupedRows = groupByDriver ? groupBy(sortedRows) : null

  /* ============================
     RENDER
  ============================ */

  return (
    <div className="delivery-data">
  <div className={classes.tableContainer}>
  <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {headCells.map(cell => (
              /*<TableCell key={cell.id} className={classes.centerCell}>*/
              <TableCell
              key={cell.id}
              className={`${classes.centerCell} ${
                isFilterActive(cell.id) ? classes.activeHeader : ''
              }`}
            >
                <div className={classes.headerCell}>
                  {cell.sort ? (
                    <TableSortLabel active direction={order} onClick={toggleSort}>
                      {cell.label}
                    </TableSortLabel>
                  ) : (
                    <span>{cell.label}</span>
                  )}
                  {cell.filter && (
                    <IconButton size="small" onClick={e => openMenu(e, cell)}>
                      
                      <ArrowDropDownIcon fontSize="small" />
                    </IconButton>
                  )}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {!groupByDriver &&
            sortedRows.map((row, i) =>
              renderRow(row, i, classes, isMultiDriver)
            )
          }

          {groupByDriver &&
            Object.entries(groupedRows).map(([driver, rows]) => (
              <React.Fragment key={driver}>
                <TableRow>
                  <TableCell colSpan={headCells.length} className={classes.groupHeader}>
                    Driver: {driver} ({rows.length})
                  </TableCell>
                </TableRow>
                {rows.map((row, i) =>
                  renderRow(row, `${driver}-${i}`, classes, isMultiDriver)
                )}
              </React.Fragment>
            ))
          }
        </TableBody>
      </Table>
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{ style: { minWidth: menuAnchor?.offsetWidth } }}
      >
       {activeCell?.filter === 'text' && (
  <MenuItem disableRipple>
    <InputBase
      autoFocus
      fullWidth
      placeholder="Search"
      value={bagSearch}
      onChange={e => {
        setBagSearch(e.target.value)
        setFilters({
          ...filters,
          [activeCell.id]: e.target.value
        })
      }}
      onKeyDown={e => e.key === 'Enter' && closeMenu()}
    />
  </MenuItem>
)}

{activeCell?.filter === 'select' && (
  <>
    <MenuItem onClick={() => applyFilter(activeCell.id, '')}>
      (All)
    </MenuItem>

    {(activeCell.fixed || getDistinctValues(activeCell.id)).map(v => (

      activeCell.id === "driver" ? (

        <MenuItem
          key={v}
          onClick={() => toggleMultiFilter(activeCell.id, v)}
        >
          <Checkbox
            checked={(filters[activeCell.id] || []).includes(v)}
            size="small"
          />
          {v}
        </MenuItem>

      ) : (

        <MenuItem
          key={v}
          selected={filters[activeCell.id] === v}
          onClick={() => applyFilter(activeCell.id, v)}
        >
          {v}
        </MenuItem>

      )

    ))}
  </>
)}
      </Menu>
    </div>
  )
}

/* ============================
   ROW
============================ */
function renderRow(row, key, classes, isMultiDriver) {
  const dt = moment.utc(row.timestamp).tz("Europe/London")
  return (
    <TableRow key={key}>

      <TableCell className={classes.centerCell}>
        {row.surname || '-'}
      </TableCell>

      <TableCell className={classes.centerCell}>
        {row.barcodeEncoding ? (
          <>
            <img
              src={`data:image/png;base64,${row.barcodeEncoding}`}
              alt="barcode"
              style={{ height: 40 }}
            />
            {row.barcodeLastFourChars && (
              <div>Ending **{row.barcodeLastFourChars}</div>
            )}
          </>
        ) : (
          '-'
        )}
      </TableCell>

      <TableCell className={`${classes.centerCell} wrap-cell`}>
        {row.additionalInfo || '-'}
      </TableCell>

      <TableCell className={classes.centerCell}>
        {row.driver || '-'}
        {isMultiDriver(row) && (
          <span className={classes.asterisk}>*</span>
        )}
      </TableCell>

      <TableCell className={`${classes.centerCell} wrap-cell`}>
        {row.failureByDriver || '-'}
      </TableCell>

      <TableCell className={`${classes.centerCell} wrap-cell`}>
        {row.failureByInventory || '-'}
      </TableCell>

      <TableCell className={classes.centerCell}>
        {row.status || '-'}
      </TableCell>

      <TableCell className={classes.centerCell}>
        <div>{dt.isValid() ? dt.format('HH:mm') : '-'}</div>
        <div>{dt.isValid() ? dt.format('DD MMM YYYY') : '-'}</div>
      </TableCell>

    </TableRow>
  )
}

/* ============================
   STYLES
============================ */

const useStyles = makeStyles({
  headerCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4
  },

  centerCell: {
    textAlign: 'center'
  },

  groupHeader: {
    backgroundColor: '#efefef',
    fontWeight: 'bold',
    textAlign: 'center'
  },
  
  activeHeader: {
    backgroundColor: '#d6d6d6'
  },

  selectedItem: {
    backgroundColor: '#e0e0e0 !important'
  },

  tableContainer: {
    maxHeight: 'calc(100vh - 250px)', // dynamic height
    overflowY: 'auto'
  },

  asterisk: {
    marginLeft: 4,
    fontWeight: 'bold'
  }
})

export default BringgTable
